import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS } from '@/entities/conversation';

/**
 * Attachment rules of the composer — cpanel `ChatMessageInput.handleFileChange`,
 * ported as a pure function so every rejection reason is unit-tested.
 */

export type AttachmentVerdict =
  | { ok: true; files: File[] }
  | { ok: false; reason: 'too-large' | 'duplicate' | 'too-many' };

/**
 * Add `incoming` to `current`.
 * - over 10 MB → `too-large`;
 * - same file name already attached → `duplicate`;
 * - at the 1-file limit → `too-many`, unless `replacing` names an attached
 *   IMAGE, in which case the incoming file takes its slot (click a chip to
 *   swap the picture).
 */
export function addAttachment(
  current: readonly File[],
  incoming: File,
  replacing?: File | null,
): AttachmentVerdict {
  if (incoming.size > MAX_ATTACHMENT_BYTES) return { ok: false, reason: 'too-large' };
  if (current.some((file) => file.name === incoming.name)) {
    return { ok: false, reason: 'duplicate' };
  }

  const replacingImage = Boolean(replacing?.type.startsWith('image/'));
  if (current.length >= MAX_ATTACHMENTS && !replacingImage) {
    return { ok: false, reason: 'too-many' };
  }

  if (replacingImage && replacing) {
    const index = current.findIndex((file) => file.name === replacing.name);
    if (index === -1) return { ok: true, files: [...current] };
    const next = [...current];
    next[index] = incoming;
    return { ok: true, files: next };
  }

  return { ok: true, files: [...current, incoming] };
}

/** Whether the draft has anything the backend would accept (cpanel send guard). */
export function hasSendableContent(message: string, files: readonly File[]): boolean {
  if (files.length > 0) return true;
  return message.split('\n').some((line) => line.trim() !== '');
}
